import type { ReactNode } from 'react';
import { SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

/**
 * Sheet widths used by the créneaux sheets, following the mockup: detail sheets
 * are wider because they stack definition lists and reservation cards, while
 * the creation forms stay narrow.
 */
const SHEET_WIDTH = {
  detail: 'sm:max-w-[520px]',
  form: 'sm:max-w-[440px]',
} as const;

/**
 * Full-bleed sheet body used by every slot sheet: no padding of its own, so the
 * header, the sections and the action bar can each draw their own separators
 * edge to edge.
 *
 * @param props.size - `detail` for read sheets, `form` for creation sheets
 * @param props.className - extra classes merged on the sheet content
 * @param props.children - header, body and action bar
 * @returns the sheet content element
 */
export const SlotSheetContent = ({
  size,
  className,
  children,
}: {
  size: keyof typeof SHEET_WIDTH;
  className?: string;
  children: ReactNode;
}) => (
  <SheetContent
    side="right"
    className={cn(
      'flex w-full flex-col gap-0 p-0',
      SHEET_WIDTH[size],
      className,
    )}
  >
    {children}
  </SheetContent>
);

/**
 * Scrollable region between the sheet header and its action bar.
 *
 * @param props.className - extra classes merged on the scroll container
 * @param props.children - the sheet sections
 * @returns the scrollable body element
 */
export const SlotSheetBody = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn('min-h-0 flex-1 overflow-y-auto', className)}>
    {children}
  </div>
);

/**
 * Sticky action bar pinned to the bottom of the sheet.
 *
 * @param props.className - extra classes merged on the bar
 * @param props.children - the action buttons
 * @returns the action bar element
 */
export const SlotSheetActions = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn('border-t bg-background p-4', className)}>{children}</div>
);
